/**
 * Cart item component for the main cart page
 * 
 * Handles quantity changes and removal of cart items with loading states.
 * Uses Shopify's section rendering API for smooth updates without page reload.
 * 
 * @module cart-item
 * @version 1.3.0
 * @extends HTMLElement
 */

const COMPONENT_NAME = 'cart-item';

if (!customElements.get(COMPONENT_NAME)) {
    class CartItem extends HTMLElement {

        static isUpdating = false;

        constructor() {
            super();
            this.lineKey = this.dataset.key;
            this.lineIndex = this.dataset.index;
        }

        connectedCallback() {
            try {
                const components = JSON.parse(this.dataset.components || '[]');
                this.querySelectorAll('[data-cart-addon-label]').forEach((label) => {
                    const property = label.dataset.cartAddonLabel;
                    const component = components.find((item) => item.properties && item.properties[property]);
                    if (component) label.textContent = window.theme.money.roundLabel(component.properties[property]);
                });
            } catch (error) {
                this.dataset.addonLabelState = 'unavailable';
            }
            this.quantitySelector = this.querySelector('quantity-selector');

            if (this.quantitySelector) {
                this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);
                this.boundHandleRemove = this.handleRemove.bind(this);
                
                this.quantitySelector.addEventListener('quantity:change', this.boundHandleQuantityChange);
                this.quantitySelector.addEventListener('quantity:remove', this.boundHandleRemove);
            }
        }

        disconnectedCallback() {
            if (this.quantitySelector) {
                this.quantitySelector.removeEventListener('quantity:change', this.boundHandleQuantityChange);
                this.quantitySelector.removeEventListener('quantity:remove', this.boundHandleRemove);
            }
        }

        /**
         * Find the cart section container
         */
        static getCartSection() {
            return document.querySelector('[id^="shopify-section-"][id*="cart"]') ||
                   document.getElementById('shopify-section-cart-section') ||
                   document.getElementById('shopify-section-main-cart');
        }

        /**
         * Get section ID from the cart section element
         */
        static getSectionId() {
            const section = CartItem.getCartSection();
            if (section && section.id) {
                return section.id.replace('shopify-section-', '');
            }
            return 'cart-section';
        }

        /**
         * Show loading state on the cart page
         */
        static showLoading() {
            CartItem.isUpdating = true;
            
            const cartSection = CartItem.getCartSection();
            if (cartSection) {
                cartSection.classList.add('is-loading');
            }
        }

        /**
         * Hide loading state on the cart page
         */
        static hideLoading() {
            CartItem.isUpdating = false;
            
            const cartSection = CartItem.getCartSection();
            if (cartSection) {
                cartSection.classList.remove('is-loading');
            }
        }

        /**
         * Update cart using Shopify's Cart API with section rendering
         * 
         * @param {Object} body - The request body for cart update
         */
        static async updateCart(body) {
            const sectionId = CartItem.getSectionId();
            
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...body,
                    sections: sectionId,
                    sections_url: window.location.pathname
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.description || 'Failed to update cart');
            }

            return response.json();
        }

        /**
         * Update the cart section HTML
         * 
         * @param {Object} responseData - The response from cart API with sections
         */
        static updateCartSection(responseData) {
            const sectionId = CartItem.getSectionId();
            const cartSection = CartItem.getCartSection();
            
            if (cartSection && responseData.sections && responseData.sections[sectionId]) {
                const newHtml = responseData.sections[sectionId];
                
                // Create a temporary container to parse the new HTML
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = newHtml;
                
                // Get the inner content (Shopify returns the section wrapper)
                const newContent = tempDiv.firstElementChild;
                if (newContent) {
                    cartSection.innerHTML = newContent.innerHTML;
                } else {
                    cartSection.innerHTML = newHtml;
                }
                
                // Remove loading class after update
                CartItem.hideLoading();
            } else {
                // Fallback: reload the page
                window.location.reload();
            }
        }

        /**
         * Handle quantity change event
         * 
         * @param {CustomEvent} event - The quantity change event
         */
        async handleQuantityChange(event) {
            if (CartItem.isUpdating) return;
            
            event.preventDefault();
            event.stopPropagation();
            
            const newQuantity = event.detail.value;
            
            CartItem.showLoading();

            try {
                const responseData = await CartItem.updateCart({
                    id: this.lineKey,
                    quantity: newQuantity
                });
                
                // Trigger cart change event
                window.dispatchEvent(new CustomEvent('cart-change', { detail: { cart: responseData } }));
                
                // Update the cart section
                CartItem.updateCartSection(responseData);
                
            } catch (error) {
                console.error('Error updating cart item:', error);
                CartItem.hideLoading();
                alert(error.message || 'Failed to update item quantity');
            }
        }

        /**
         * Handle remove event
         * 
         * @param {CustomEvent} event - The remove event
         */
        async handleRemove(event) {
            if (CartItem.isUpdating) return;
            
            event.preventDefault();
            event.stopPropagation();
            
            CartItem.showLoading();

            try {
                const responseData = await CartItem.updateCart({
                    id: this.lineKey,
                    quantity: 0
                });
                
                // Trigger cart change event
                window.dispatchEvent(new CustomEvent('cart-change', { detail: { cart: responseData } }));
                
                // Update the cart section
                CartItem.updateCartSection(responseData);
                
            } catch (error) {
                console.error('Error removing cart item:', error);
                CartItem.hideLoading();
                alert(error.message || 'Failed to remove item');
            }
        }
    }

    customElements.define(COMPONENT_NAME, CartItem);
}
